// Enterprise documentation content model.
// Each doc page is authored as an ordered list of content blocks rendered by DocContent.jsx.

export const DOC_VERSIONS = [
  { id: "2.4", label: "v2.4 (Current)", current: true },
  { id: "2.3", label: "v2.3" },
  { id: "1.x", label: "v1.x (Legacy)" },
];

export const GOV_OS_SHOTS = {
  dashboard: "https://static.prod-images.emergentagent.com/jobs/7f963655-bbc3-490d-b05c-c6084837c7f3/images/9996fdfeb09e81abfe8acfd0cc71b86187974ad9d461436f40ee433c2375212a.jpeg",
  risk: "https://static.prod-images.emergentagent.com/jobs/7f963655-bbc3-490d-b05c-c6084837c7f3/images/8917c323101e5b37557de60d8a64af8683333072f910f797dd93f2e751486c31.jpeg",
  evidence: "https://static.prod-images.emergentagent.com/jobs/7f963655-bbc3-490d-b05c-c6084837c7f3/images/349b38cdb40ebbe943599b29e8edc57753a129303e717a4b8defd4c27fed13eb.jpeg",
};

// Sidebar structure — category → ordered doc slugs.
export const DOC_NAV = [
  {
    category: "Getting Started",
    items: ["overview", "installation", "configuration"],
  },
  {
    category: "Platform Architecture",
    items: ["architecture", "deployment", "administration", "operations", "api"],
  },
  {
    category: "Governance Framework",
    items: ["governance-framework", "compliance-mapping"],
  },
  {
    category: "AI Governance OS",
    items: [
      "governance-os-overview",
      "governance-os-modules",
      "feature-matrix",
      "deployment-models",
      "administrator-guide",
      "executive-guide",
      "api-integration",
    ],
  },
  {
    category: "Reference",
    items: ["release-notes"],
  },
];

const A = "AstroLife™ AI Governance OS";

export const DOCS = {
  overview: {
    title: "Documentation Overview",
    category: "Getting Started",
    description:
      "Introduction to the AstroLife™ documentation platform: how it is organised, who it is for, and how to navigate architecture, deployment, administration and API references.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: `Welcome to the ${A} documentation. This platform provides everything engineering, governance and executive teams need to install, operate and audit verifiable AI governance at scale — across sovereign, on-premise and cloud environments.` },
      { type: "callout", tone: "info", title: "New here?", text: "Start with Installation, then Configuration, then the Architecture reference. Governance leads should read the Governance Framework and Compliance Mapping." },
      { type: "h", text: "How the documentation is organised" },
      { type: "cards", items: [
        { title: "Platform Architecture", text: "System design, the evidence chain, deployment topologies and the administration & operations model.", to: "/documentation/architecture" },
        { title: "Governance Framework", text: "The control model behind verifiable governance and how it maps to EU and international standards.", to: "/documentation/governance-framework" },
        { title: "AI Governance OS", text: "Module documentation, deployment models, administrator and executive guides, and API integration.", to: "/documentation/governance-os-overview" },
        { title: "API Documentation", text: "REST endpoints, authentication, webhooks and SDK examples for programmatic governance.", to: "/documentation/api" },
      ] },
      { type: "h", text: "Audience" },
      { type: "list", items: [
        "Platform & infrastructure engineers deploying and operating the system.",
        "Governance, risk and compliance (GRC) leads defining controls and evidence.",
        "Executives and boards accountable for AI oversight and reporting.",
        "Developers integrating governance into MLOps and business systems via the API.",
      ] },
      { type: "h", text: "Versioning" },
      { type: "p", text: "Documentation is versioned alongside the platform. Use the version selector at the top of the sidebar to switch between the current release and legacy versions. Unless noted, content reflects the current release." },
    ],
    related: ["installation", "architecture", "governance-os-overview"],
  },

  installation: {
    title: "Installation",
    category: "Getting Started",
    description: "Install the AstroLife™ AI Governance OS in sovereign, on-premise or cloud environments, including prerequisites and a verified first-run checklist.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "This guide walks through a standard installation. The platform ships as a set of containerised services orchestrated via Kubernetes (Helm) or a hardened single-node appliance for air-gapped deployments." },
      { type: "h", text: "Prerequisites" },
      { type: "table", headers: ["Component", "Minimum", "Recommended"], rows: [
        ["Kubernetes", "1.27", "1.29+"],
        ["CPU", "16 vCPU", "32 vCPU"],
        ["Memory", "32 GB", "64 GB"],
        ["Storage", "250 GB SSD", "1 TB NVMe"],
        ["Datastore", "PostgreSQL 14 / compatible", "PostgreSQL 15 HA"],
      ] },
      { type: "h", text: "Install with Helm" },
      { type: "steps", items: [
        { title: "Add the chart repository", text: "Register the AstroLife OCI registry and pull the signed chart. All artefacts are cosign-signed for supply-chain integrity." },
        { title: "Create a values file", text: "Provide your domain, datastore connection, object storage and identity provider (OIDC/SAML) settings." },
        { title: "Deploy the release", text: "Run the install into a dedicated namespace. Services come up in dependency order and expose readiness probes." },
        { title: "Verify the first run", text: "Confirm all pods are Ready, run the built-in health check, and sign in as the bootstrap administrator to complete setup." },
      ] },
      { type: "code", lang: "bash", caption: "Install with Helm", code: `helm registry login oci.astrolife.es
helm install governance-os oci://oci.astrolife.es/charts/governance-os \\
  --namespace astrolife --create-namespace \\
  --values ./values.production.yaml

kubectl -n astrolife get pods
kubectl -n astrolife exec deploy/governance-os-api -- governance-os healthcheck` },
      { type: "callout", tone: "warn", title: "Air-gapped installs", text: "For sovereign/air-gapped environments, mirror the signed image bundle to your internal registry and use the offline values profile. See Deployment for topology guidance." },
      { type: "h", text: "First-run checklist" },
      { type: "list", items: [
        "All services report Ready and pass the healthcheck.",
        "Identity provider (OIDC/SAML) connected and a test login succeeds.",
        "Object storage reachable and the evidence store initialises.",
        "Backup schedule and retention policy configured.",
      ] },
    ],
    related: ["configuration", "deployment", "administration"],
  },

  configuration: {
    title: "Configuration",
    category: "Getting Started",
    description: "Configure identity, tenancy, data residency, retention and notification settings for the AstroLife™ AI Governance OS.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "Configuration is declarative. Settings are managed through a versioned configuration object, applied via GitOps or the administration console, and every change is itself recorded as governance evidence." },
      { type: "h", text: "Core configuration domains" },
      { type: "table", headers: ["Domain", "What it controls"], rows: [
        ["Identity", "OIDC/SAML providers, SCIM provisioning, session policy."],
        ["Tenancy", "Organisations, business units and data isolation boundaries."],
        ["Residency", "Region pinning and sovereign storage locations."],
        ["Retention", "Evidence and log retention windows and legal holds."],
        ["Notifications", "Email, webhook and SIEM routing for governance events."],
      ] },
      { type: "code", lang: "yaml", caption: "config.production.yaml (excerpt)", code: `identity:
  provider: oidc
  issuer: https://id.example.gov
  scimEnabled: true
residency:
  region: eu-central
  sovereignStorage: true
retention:
  evidenceDays: 3650
  logsDays: 730
notifications:
  siem:
    protocol: syslog-tls
    endpoint: siem.internal:6514` },
      { type: "callout", tone: "info", title: "Change as evidence", text: "Applying configuration produces a signed evidence record with the actor, timestamp and diff — configuration drift is detectable and auditable." },
    ],
    related: ["installation", "administration", "governance-framework"],
  },

  architecture: {
    title: "System Architecture",
    category: "Platform Architecture",
    description: "The AstroLife™ AI Governance OS architecture: control plane, governance engine, verifiable evidence chain and integration surfaces.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "The platform is built as a layered, event-sourced system. Every governance-relevant action is captured as an immutable, cryptographically linked event, producing a defensible evidence chain from data to decision to audit." },
      { type: "diagram", title: "Governance evidence chain", layers: [
        { name: "Sources", nodes: ["AI Inventory", "MLOps / Pipelines", "Business Systems"] },
        { name: "Governance Engine", nodes: ["Risk", "Lifecycle", "Human Oversight", "Controls"] },
        { name: "Evidence Layer", nodes: ["Immutable Event Log", "Cryptographic Linking", "Evidence Store"] },
        { name: "Assurance", nodes: ["Audit Readiness", "Reporting", "Attestation / Export"] },
      ] },
      { type: "h", text: "Key components" },
      { type: "cards", items: [
        { title: "Control plane", text: "Orchestrates tenancy, identity, configuration and policy across all modules." },
        { title: "Governance engine", text: "Executes risk, lifecycle, oversight and control workflows as event-sourced processes." },
        { title: "Evidence layer", text: "Append-only, hash-linked event log with tamper-evidence and long-term retention." },
        { title: "Integration surface", text: "REST API, webhooks and SIEM/MLOps connectors for programmatic governance." },
      ] },
      { type: "h", text: "Why event sourcing" },
      { type: "list", items: [
        "Every state change is reconstructable and independently verifiable.",
        "Evidence cannot be silently altered — the chain is tamper-evident.",
        "Point-in-time reconstruction supports audits and investigations.",
      ] },
    ],
    related: ["deployment", "api", "governance-os-architecture"],
  },

  deployment: {
    title: "Deployment Guide",
    category: "Platform Architecture",
    description: "Deployment topologies for the AstroLife™ AI Governance OS: sovereign air-gapped, on-premise, private cloud and hybrid.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "The platform supports four primary topologies. All maintain the same governance guarantees; they differ in where data and compute reside." },
      { type: "table", headers: ["Topology", "Data residency", "Best for"], rows: [
        ["Sovereign (air-gapped)", "Fully on-premise, no egress", "Defence, government, classified"],
        ["On-premise", "Customer data centre", "Regulated enterprises"],
        ["Private cloud", "Customer cloud tenancy (EU region)", "Cloud-first regulated orgs"],
        ["Hybrid", "Control plane cloud, evidence on-premise", "Phased sovereignty"],
      ] },
      { type: "h", text: "High availability" },
      { type: "steps", items: [
        { title: "Run stateless services with multiple replicas", text: "The API and engine are horizontally scalable behind an internal load balancer." },
        { title: "Use an HA datastore", text: "Deploy PostgreSQL with synchronous replication and automated failover." },
        { title: "Replicate the evidence store", text: "Object storage is replicated within the residency boundary with versioning and immutability locks." },
        { title: "Validate disaster recovery", text: "Test restore from backup and verify evidence-chain integrity after recovery." },
      ] },
      { type: "callout", tone: "success", title: "Zero-egress guarantee", text: "In sovereign mode the platform operates with no outbound connectivity. Updates are delivered as signed offline bundles." },
    ],
    related: ["installation", "deployment-models", "operations"],
  },

  administration: {
    title: "Administration Guide",
    category: "Platform Architecture",
    description: "Administer users, roles, tenancy, policies and integrations across the AstroLife™ AI Governance OS.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "Administrators manage identity, access, tenancy and platform policy. All administrative actions are role-gated and recorded as evidence." },
      { type: "h", text: "Role-based access control" },
      { type: "table", headers: ["Role", "Capabilities"], rows: [
        ["Platform Admin", "Full configuration, identity, tenancy and integrations."],
        ["Governance Lead", "Define controls, approve risk decisions, manage evidence policy."],
        ["Risk Owner", "Own and mitigate assigned risks; submit oversight records."],
        ["Auditor", "Read-only access to evidence, logs and reports."],
        ["Executive", "Dashboards, posture and reporting only."],
      ] },
      { type: "h", text: "Common administrative tasks" },
      { type: "steps", items: [
        { title: "Provision users", text: "Connect SCIM or invite users, then assign roles scoped to organisations or business units." },
        { title: "Define policy", text: "Set risk thresholds, oversight cadence and evidence retention per tenant." },
        { title: "Connect integrations", text: "Wire up SIEM, MLOps and ticketing connectors to route governance events." },
        { title: "Review the audit log", text: "Monitor administrative activity through the immutable audit trail." },
      ] },
    ],
    related: ["configuration", "operations", "administrator-guide"],
  },

  operations: {
    title: "Operations Guide",
    category: "Platform Architecture",
    description: "Day-2 operations for the AstroLife™ AI Governance OS: monitoring, backups, upgrades, incident response and observability.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "This guide covers ongoing operations once the platform is live. The system is designed for low-touch operation with strong observability." },
      { type: "h", text: "Monitoring & observability" },
      { type: "list", items: [
        "Prometheus metrics for all services with pre-built Grafana dashboards.",
        "Structured logs shipped to your SIEM over syslog-TLS.",
        "Synthetic health checks and evidence-chain integrity probes.",
      ] },
      { type: "h", text: "Backups & upgrades" },
      { type: "steps", items: [
        { title: "Schedule backups", text: "Automate datastore and evidence-store backups with tested restore procedures." },
        { title: "Stage upgrades", text: "Apply signed release bundles to a staging namespace and run the verification suite." },
        { title: "Roll forward", text: "Perform rolling upgrades with zero downtime; evidence continuity is preserved across versions." },
      ] },
      { type: "callout", tone: "warn", title: "Integrity checks", text: "Run the evidence-chain integrity check after every upgrade and restore. Any break is surfaced immediately and blocks attestation until resolved." },
    ],
    related: ["deployment", "administration", "release-notes"],
  },

  api: {
    title: "API Documentation",
    category: "Platform Architecture",
    description: "REST API reference for the AstroLife™ AI Governance OS: authentication, resources, webhooks and error handling.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "The platform exposes a versioned REST API for programmatic governance. All endpoints require authentication and are subject to role-based authorisation and rate limiting." },
      { type: "h", text: "Authentication" },
      { type: "code", lang: "bash", caption: "Obtain an access token", code: `curl -X POST https://governance.example.gov/api/v1/auth/token \\
  -H "Content-Type: application/json" \\
  -d '{ "client_id": "svc-mlops", "client_secret": "***" }'
# => { "access_token": "eyJ...", "expires_in": 3600 }` },
      { type: "h", text: "Core resources" },
      { type: "table", headers: ["Method", "Endpoint", "Description"], rows: [
        ["GET", "/api/v1/systems", "List registered AI systems."],
        ["POST", "/api/v1/systems", "Register a new AI system."],
        ["GET", "/api/v1/risks", "List risks with filters."],
        ["POST", "/api/v1/evidence", "Append a governance evidence record."],
        ["GET", "/api/v1/reports/posture", "Retrieve current governance posture."],
      ] },
      { type: "h", text: "Webhooks" },
      { type: "p", text: "Subscribe to governance events (risk raised, control failed, oversight recorded) delivered as signed webhooks for real-time integration with your systems." },
      { type: "code", lang: "json", caption: "Example webhook payload", code: `{
  "event": "risk.raised",
  "systemId": "sys_4f2a",
  "severity": "high",
  "actor": "risk-engine",
  "occurredAt": "2026-06-02T09:41:00Z",
  "evidenceHash": "sha256:9b3f…"
}` },
    ],
    related: ["api-integration", "architecture", "administration"],
  },

  "governance-framework": {
    title: "Governance Framework",
    category: "Governance Framework",
    description: "The AstroLife™ verifiable AI governance control model spanning inventory, risk, lifecycle, human oversight, evidence and continuous compliance.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "Verifiable AI governance is more than a policy document. AstroLife implements governance as a set of enforceable, evidence-producing controls operating continuously across the AI lifecycle." },
      { type: "h", text: "The six control domains" },
      { type: "cards", items: [
        { title: "AI Inventory", text: "A live, authoritative register of every AI system, model and use case in scope." },
        { title: "Risk Management", text: "Structured identification, assessment, mitigation and acceptance of AI risk." },
        { title: "Lifecycle Governance", text: "Controls at every stage from design to decommission, with gated approvals." },
        { title: "Human Oversight", text: "Meaningful human control as a first-class, evidenced governance control." },
        { title: "Evidence & Traceability", text: "An immutable chain linking controls and decisions to durable records." },
        { title: "Continuous Compliance", text: "Ongoing monitoring against obligations rather than point-in-time checks." },
      ] },
      { type: "callout", tone: "info", title: "Evidence-first", text: "Every control produces evidence by design. Compliance is demonstrated with records, not asserted in prose." },
    ],
    related: ["compliance-mapping", "governance-os-overview", "executive-guide"],
  },

  "compliance-mapping": {
    title: "Compliance Mapping",
    category: "Governance Framework",
    description: "How AstroLife™ governance controls map to the EU AI Act, ISO/IEC 42001, ISO/IEC 23894 and the NIST AI Risk Management Framework.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "AstroLife controls are mapped to major AI governance frameworks so that a single control implementation satisfies obligations across multiple standards, with evidence attached." },
      { type: "table", headers: ["AstroLife control", "EU AI Act", "ISO 42001", "ISO 23894", "NIST AI RMF"], rows: [
        ["AI Inventory", "Art. 9, 11", "A.6", "6.2", "MAP"],
        ["Risk Management", "Art. 9", "A.8", "6.4", "MEASURE"],
        ["Human Oversight", "Art. 14", "A.9", "6.5", "GOVERN"],
        ["Evidence & Traceability", "Art. 12, 20", "A.10", "6.6", "MANAGE"],
        ["Transparency", "Art. 13", "A.7", "6.3", "MAP"],
        ["Continuous Compliance", "Art. 61", "A.9.3", "6.7", "MANAGE"],
      ] },
      { type: "callout", tone: "success", title: "Map once, comply many", text: "Because controls are mapped to multiple frameworks, evidence generated for one obligation is reusable across audits — reducing duplicated effort." },
    ],
    related: ["governance-framework", "feature-matrix", "executive-guide"],
  },

  // ── AI Governance OS product docs ──
  "governance-os-overview": {
    title: "AI Governance OS — Overview",
    category: "AI Governance OS",
    description: "Product overview of the AstroLife™ AI Governance OS: the operating system for verifiable, sovereign AI governance.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: `${A} is the operating system for AI governance. It unifies inventory, risk, lifecycle, oversight, evidence and reporting into a single, auditable platform — deployable in sovereign, on-premise and cloud environments.` },
      { type: "image", src: GOV_OS_SHOTS.dashboard, alt: "AI Governance OS posture dashboard", caption: "Governance posture overview: systems, open risks, audit readiness and evidence records." },
      { type: "h", text: "What it does" },
      { type: "list", items: [
        "Maintains a live inventory of every AI system and use case.",
        "Runs structured risk and lifecycle governance with gated approvals.",
        "Captures human oversight as evidenced, first-class control.",
        "Produces an immutable, verifiable evidence chain for audit and attestation.",
        "Reports governance posture to executives and regulators in real time.",
      ] },
      { type: "cards", items: [
        { title: "Module documentation", text: "Explore each governance module in depth.", to: "/documentation/governance-os-modules" },
        { title: "Feature matrix", text: "Compare capabilities across editions and deployment models.", to: "/documentation/feature-matrix" },
        { title: "Deployment models", text: "Sovereign, on-premise, private cloud and hybrid.", to: "/documentation/deployment-models" },
      ] },
    ],
    related: ["governance-os-modules", "feature-matrix", "administrator-guide"],
  },

  "governance-os-modules": {
    title: "Modules",
    category: "AI Governance OS",
    description: "Detailed documentation of the AI Governance OS modules: Inventory, Risk, Lifecycle, Human Oversight, Evidence and Reporting.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "The platform is composed of tightly integrated modules that share a common evidence layer. Each module can be adopted incrementally." },
      { type: "h", text: "Inventory" },
      { type: "p", text: "An authoritative register of AI systems, models, datasets and use cases — the foundation for all downstream governance." },
      { type: "h", text: "Risk" },
      { type: "image", src: GOV_OS_SHOTS.risk, alt: "AI risk register", caption: "Risk register with severity, ownership, mitigation and linked evidence." },
      { type: "p", text: "Structured risk identification, assessment and mitigation with clear ownership, due dates and status — every change evidenced." },
      { type: "h", text: "Lifecycle" },
      { type: "p", text: "Governance gates across design, development, validation, deployment, monitoring and decommission, with required approvals at each stage." },
      { type: "h", text: "Human Oversight" },
      { type: "p", text: "Records meaningful human control — reviews, overrides and sign-offs — as evidence, satisfying Article 14 of the EU AI Act." },
      { type: "h", text: "Evidence" },
      { type: "image", src: GOV_OS_SHOTS.evidence, alt: "Verifiable evidence chain", caption: "Cryptographically linked evidence chain with export-to-attestation." },
      { type: "p", text: "An append-only, hash-linked log that makes governance tamper-evident and independently verifiable." },
      { type: "h", text: "Reporting" },
      { type: "p", text: "Real-time posture dashboards and exportable evidence packs for boards, auditors and regulators." },
    ],
    related: ["governance-os-overview", "feature-matrix", "administrator-guide"],
  },

  "feature-matrix": {
    title: "Feature Matrix",
    category: "AI Governance OS",
    description: "Capability and edition feature matrix for the AstroLife™ AI Governance OS.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "The feature matrix compares capabilities across editions. All editions share the verifiable evidence core." },
      { type: "table", check: true, headers: ["Capability", "Standard", "Enterprise", "Sovereign"], rows: [
        ["AI Inventory", true, true, true],
        ["Risk & Lifecycle Governance", true, true, true],
        ["Verifiable Evidence Chain", true, true, true],
        ["Human Oversight Records", true, true, true],
        ["SIEM / MLOps Integrations", false, true, true],
        ["Multi-tenant & Business Units", false, true, true],
        ["Air-gapped Deployment", false, false, true],
        ["Offline Signed Updates", false, false, true],
        ["Dedicated Sovereign Support", false, false, true],
      ] },
      { type: "callout", tone: "info", title: "Choosing an edition", text: "Regulated enterprises typically start with Enterprise; defence and government workloads requiring zero egress use Sovereign." },
    ],
    related: ["deployment-models", "compliance-mapping", "governance-os-overview"],
  },

  "deployment-models": {
    title: "Deployment Models",
    category: "AI Governance OS",
    description: "Deployment models for the AI Governance OS: sovereign air-gapped, on-premise, private cloud and hybrid.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "Every deployment model delivers identical governance guarantees. The difference is where data and compute reside and how updates are delivered." },
      { type: "diagram", title: "Deployment topologies", layers: [
        { name: "Sovereign", nodes: ["Air-gapped", "Zero egress", "Offline updates"] },
        { name: "On-premise", nodes: ["Customer DC", "Full control", "HA datastore"] },
        { name: "Private cloud", nodes: ["EU tenancy", "Region pinning", "Managed scaling"] },
        { name: "Hybrid", nodes: ["Cloud control plane", "On-prem evidence", "Phased sovereignty"] },
      ] },
      { type: "cards", items: [
        { title: "Sovereign", text: "Fully air-gapped for defence, government and classified workloads." },
        { title: "On-premise", text: "Runs in your data centre with full operational control." },
        { title: "Private cloud", text: "Your EU cloud tenancy with region pinning and sovereign storage." },
        { title: "Hybrid", text: "Cloud control plane with on-premise evidence for phased sovereignty." },
      ] },
    ],
    related: ["deployment", "feature-matrix", "installation"],
  },

  "administrator-guide": {
    title: "Administrator Guide",
    category: "AI Governance OS",
    description: "Administrator guide for the AI Governance OS: onboarding, roles, policy and integrations.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "This guide helps administrators operationalise the platform after installation — from onboarding teams to wiring integrations." },
      { type: "steps", items: [
        { title: "Onboard your organisation", text: "Create your organisation, business units and residency boundaries." },
        { title: "Connect identity", text: "Integrate OIDC/SAML and enable SCIM for automated provisioning." },
        { title: "Assign roles", text: "Map users to Platform Admin, Governance Lead, Risk Owner, Auditor or Executive roles." },
        { title: "Define governance policy", text: "Set risk thresholds, oversight cadence and evidence retention." },
        { title: "Wire integrations", text: "Connect SIEM, MLOps and ticketing to route governance events." },
        { title: "Publish dashboards", text: "Share executive posture dashboards with stakeholders." },
      ] },
      { type: "callout", tone: "warn", title: "Least privilege", text: "Assign the narrowest role that lets each user do their job. Auditors should always be read-only." },
    ],
    related: ["administration", "governance-os-modules", "api-integration"],
  },

  "executive-guide": {
    title: "Executive Guide",
    category: "AI Governance OS",
    description: "Executive guide to AI governance with AstroLife™: oversight, accountability, reporting and regulatory readiness.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "AI governance is a board-level responsibility. This guide explains how executives use the platform to discharge oversight duties with confidence." },
      { type: "h", text: "What executives get" },
      { type: "list", items: [
        "A real-time view of AI governance posture across the organisation.",
        "Clear accountability: every risk and control has a named owner.",
        "Regulator-ready evidence packs on demand — no fire drills.",
        "Early warning when controls fail or risks exceed thresholds.",
      ] },
      { type: "h", text: "Discharging oversight" },
      { type: "steps", items: [
        { title: "Review posture", text: "Use the executive dashboard to see systems in scope, open risks and audit readiness." },
        { title: "Confirm accountability", text: "Verify that high-impact systems have assigned owners and current oversight records." },
        { title: "Attest with evidence", text: "Export signed evidence packs to demonstrate compliance to boards and regulators." },
      ] },
      { type: "callout", tone: "success", title: "Defensible governance", text: "When challenged, you can show exactly which controls applied, who acted and when — backed by a tamper-evident record." },
    ],
    related: ["governance-framework", "compliance-mapping", "governance-os-overview"],
  },

  "api-integration": {
    title: "API Integration Examples",
    category: "AI Governance OS",
    description: "Practical API integration examples for embedding AI governance into MLOps and business systems.",
    updated: "2026-06-02",
    blocks: [
      { type: "p", text: "These examples show how to integrate governance directly into your delivery pipelines so that governance happens automatically, not as an afterthought." },
      { type: "h", text: "Register a model from your pipeline" },
      { type: "code", lang: "python", caption: "Python — register an AI system", code: `import requests

BASE = "https://governance.example.gov/api/v1"
token = get_service_token()  # OIDC client-credentials

resp = requests.post(
    f"{BASE}/systems",
    headers={"Authorization": f"Bearer {token}"},
    json={
        "name": "credit-risk-scorer",
        "riskTier": "high",
        "owner": "risk-team@example.gov",
        "lifecycleStage": "validation",
    },
)
resp.raise_for_status()
system_id = resp.json()["id"]` },
      { type: "h", text: "Append evidence on deployment" },
      { type: "code", lang: "javascript", caption: "Node.js — append an evidence record", code: `import { GovernanceClient } from "@astrolife/governance-sdk";

const client = new GovernanceClient({ baseUrl: process.env.GOV_URL, token });

await client.evidence.append({
  systemId,
  type: "deployment.approved",
  actor: "ci-pipeline",
  detail: { version: "1.4.2", approver: "gov-lead@example.gov" },
});` },
      { type: "callout", tone: "info", title: "SDKs", text: "Official SDKs are available for Python, Node.js and Java. All calls are authenticated, authorised and rate-limited." },
    ],
    related: ["api", "administrator-guide", "governance-os-modules"],
  },

  "release-notes": {
    title: "Release Notes",
    category: "Reference",
    description: "Release notes and changelog for the AstroLife™ AI Governance OS.",
    updated: "2026-06-02",
    blocks: [
      { type: "h", text: "v2.4 — June 2026 (Current)" },
      { type: "list", items: [
        "Verifiable evidence chain now supports offline attestation export.",
        "New compliance mapping for ISO/IEC 23894 and NIST AI RMF.",
        "Executive dashboard redesign with posture trend analysis.",
        "SCIM 2.0 provisioning and improved SAML support.",
      ] },
      { type: "h", text: "v2.3 — March 2026" },
      { type: "list", items: [
        "Hybrid deployment model (cloud control plane, on-premise evidence).",
        "Webhook signing and delivery retries.",
        "Performance improvements to the risk engine.",
      ] },
      { type: "h", text: "v1.x — Legacy" },
      { type: "p", text: "Legacy 1.x releases are in extended support. Upgrade to 2.x is recommended; evidence continuity is preserved across the upgrade." },
    ],
    related: ["operations", "overview"],
  },
};

// Flat searchable index built from DOCS.
export function buildDocIndex() {
  return Object.entries(DOCS).map(([slug, doc]) => {
    const text = (doc.blocks || [])
      .map((b) => b.text || b.title || (b.items || []).map((i) => i.title || i.text || i.term || i).join(" ") || "")
      .join(" ");
    return { slug, title: doc.title, category: doc.category, description: doc.description, text: `${doc.title} ${doc.description} ${text}`.toLowerCase() };
  });
}
